import { gql } from "../../__generated__";
import BottomTab from "../../components/common/bottom-tab";
import { useNavigate } from "react-router-dom";
import NoOrders from "../../components/order/no-orders";
import { useQuery } from "@apollo/client";
import Partition from "../../components/common/partition";
import Header from "../../components/common/header";
import ScrollRestore from "../../components/common/scroll-restore";
import { HiCheckCircle, HiChevronRight, HiStar } from "react-icons/hi2";
import OrderList from "./order-list";
import { GiLaurelCrown } from "react-icons/gi";
import { track } from "@amplitude/analytics-browser";
import FrequencyCard from "../../components/frequency/frequency-card";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MyMissionShortcut from "./components/my-mission-shortcut";

export const GET_ORDERS = gql(`
  query Orders {
    orders {
      id
      createdAt
      amount
      products {
        store {
          title
        }
        menus {
          id
          img
        }
        img
      }
      pickUpTime
      totalDiscount
      totalQuantity
      isCanceled
      isPackConfirmed
      pack {
        id
        store {
          title
          img
          reserveTime
        }
      }
      packId
      packPickupStartTime
      packPickupEndTime
      packAutoConfirmTime
      review {
        id
        status
      }
      reviewWriteStatus
    }
  }
`);

export const formatDate = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 월 (0부터 시작하므로 1을 더함)
  const day = date.getDate(); // 일
  const weekDays = ["일", "월", "화", "수", "목", "금", "토"];
  const dayOfWeek = weekDays[date.getDay()]; // 요일

  // 오늘 날짜와 주문 날짜의 년도가 다르면 년도를 표시
  if (now.getFullYear() !== year) {
    return `${year}. ${month}. ${day} (${dayOfWeek})`;
  } else {
    // 오늘 날짜와 주문 날짜의 년도가 같으면 월과 일만 표시
    return `${month}. ${day} (${dayOfWeek})`;
  }
};

export const calculateCarbonEmission = (amount: number) => {
  // 입력된 금액을 1,000원 단위로 올림
  const roundedAmount = Math.ceil(amount / 1000) * 1000;

  // 1,000원당 0.25kg을 곱해서 탄소 배출량 계산
  const carbonEmission = (roundedAmount / 1000) * 0.25;

  return carbonEmission;
};

const WritableReviewCard = ({
  totalWritableReviewOrdersCount,
  writtenReviewsCount,
}: {
  totalWritableReviewOrdersCount: number | undefined;
  writtenReviewsCount: number | undefined;
}) => {
  const navigate = useNavigate();

  if (totalWritableReviewOrdersCount !== undefined) {
    if (totalWritableReviewOrdersCount > 0)
      return (
        <>
          <div
            className="w-full bg-white p-4 flex items-center justify-between active:bg-primary/5 transition-colors"
            onClick={() => {
              track("주문내역 작성할 수 있는 리뷰 카드 클릭");
              navigate("/order/writable-review");
            }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center">
                <HiStar className="text-yellow-500 w-6 h-6" />
              </div>
              <div className="flex flex-col ">
                <span className="text-gray-800 font-semibold">
                  {totalWritableReviewOrdersCount}개의 작성할 수 있는 리뷰가
                  있어요
                </span>
                <span className="text-gray-500 text-sm">
                  리뷰를 모두 작성하고 리뷰 히어로가 되어보세요!
                </span>
              </div>
            </div>
            <HiChevronRight className="text-black w-6 h-6" />
          </div>
          <Partition color="light" height="thick" />
        </>
      );
    // 작성한 리뷰가 있고 더이상 작성 가능한 리뷰가 없으면
    else if (
      writtenReviewsCount !== undefined &&
      writtenReviewsCount > 0 &&
      totalWritableReviewOrdersCount === 0
    )
      return (
        <>
          <div className="w-full bg-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center">
                <HiCheckCircle className="text-blue-500 w-9 h-9" />
              </div>
              <div className="flex flex-col ">
                <span className="text-gray-800 font-semibold flex items-center gap-1">
                  모든 리뷰를 남겨주신 당신은, 리뷰 히어로{" "}
                  <GiLaurelCrown className="w-6 h-6" />
                </span>
                <span className="text-gray-900 text-sm">
                  모든 리뷰를 작성했어요
                </span>
              </div>
            </div>
          </div>
          <Partition color="light" height="thick" />
        </>
      );
  }
  return null;
};

const Order = () => {
  const { data, loading } = useQuery(GET_ORDERS, {
    fetchPolicy: "network-only",
  });

  const totalWritableReviewOrdersCount: number =
    data?.orders?.filter((order: any) => order.reviewWriteStatus === "가능")
      .length ?? 0;

  const writtenReviewsCount: number =
    data?.orders?.filter((order: any) => order.reviewWriteStatus === "완료")
      .length ?? 0;

  return (
    <div className="w-full h-screen flex flex-col">
      <Header title="주문내역" />

      <ScrollRestore
        id="scroll-order"
        className="pb-[64px] overflow-y-scroll"
        initRestore={data && data.orders.length > 0}
      >
        {loading ? (
          <div className="px-4 pt-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="skeleton-wave-item mb-4 bg-white p-4 rounded-lg"
                style={{
                  animationDelay: `${index * 0.15}s`,
                }}
              >
                <div className="mb-3">
                  <Skeleton width={120} height={16} />
                </div>
                <div className="flex gap-2">
                  <Skeleton width={90} height={90} borderRadius="8px" />
                  <div className="flex-1 flex flex-col gap-2">
                    <Skeleton width={150} height={20} />
                    <Skeleton width={100} height={16} />
                    <div className="flex gap-2">
                      <Skeleton width={80} height={24} borderRadius="6px" />
                      <Skeleton width={120} height={24} borderRadius="6px" />
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  <Skeleton width="100%" height={40} borderRadius="24px" />
                  <Skeleton width="100%" height={40} borderRadius="24px" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <Partition color="light" height="thin" />

        <WritableReviewCard
          totalWritableReviewOrdersCount={totalWritableReviewOrdersCount}
          writtenReviewsCount={writtenReviewsCount}
        />

        <MyMissionShortcut />

        <div className="w-full py-4">
          {data?.orders?.length && data?.orders?.length > 0 ? (
            <FrequencyCard />
          ) : null}
        </div>

        <Partition color="light" height="thin" />

        <OrderList order={data?.orders as any[]} />
        {data?.orders?.length === 0 ? <NoOrders /> : null}
      </ScrollRestore>
      <BottomTab />
    </div>
  );
};

export default Order;
